// Firebase configuration
import { initializeApp } from 'firebase/app';
import { getAuth, connectAuthEmulator } from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';
import { getFunctions, connectFunctionsEmulator } from 'firebase/functions';

// Build Firebase config dynamically - only include what's available
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: `${process.env.REACT_APP_FIREBASE_PROJECT_ID}.firebaseapp.com`,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
};

// Only add optional services if environment variables are provided
if (process.env.REACT_APP_FIREBASE_STORAGE_BUCKET) {
  firebaseConfig.storageBucket = process.env.REACT_APP_FIREBASE_STORAGE_BUCKET;
}

if (process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID) {
  firebaseConfig.messagingSenderId = process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID;
}

if (process.env.REACT_APP_FIREBASE_APP_ID) {
  firebaseConfig.appId = process.env.REACT_APP_FIREBASE_APP_ID;
}

// Validate that required environment variables are present
const requiredEnvVars = [
  'REACT_APP_FIREBASE_API_KEY',
  'REACT_APP_FIREBASE_PROJECT_ID'
];

const missingVars = requiredEnvVars.filter(varName => !process.env[varName]);

if (missingVars.length > 0) {
  console.error('Missing required Firebase environment variables:', missingVars);
  console.error('Please create a .env file in the frontend directory with:');
  console.error('- REACT_APP_FIREBASE_API_KEY');
  console.error('- REACT_APP_FIREBASE_PROJECT_ID');
  console.error('\nOptional variables (for additional features):');
  console.error('- REACT_APP_FIREBASE_APP_ID (for app identification)');
  console.error('- REACT_APP_FIREBASE_MESSAGING_SENDER_ID (for push notifications)');
  console.error('- REACT_APP_FIREBASE_STORAGE_BUCKET (for file storage)');
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

// Initialize Firebase services
export const auth = getAuth(app);
// We standardize on the non-default Firestore database: offmarkets-test
// (can be overridden via REACT_APP_FIREBASE_DATABASE_ID if needed)
const databaseId = process.env.REACT_APP_FIREBASE_DATABASE_ID || 'offmarkets-test';
export const db = getFirestore(app, databaseId);
export const storage = getStorage(app);
export const functions = getFunctions(app, 'us-central1');

// Auto-connect to emulators in development (when available)
// We ping each emulator first so we only connect if it's actually running.
const EMULATOR_HOST = process.env.REACT_APP_FIREBASE_EMULATOR_HOST || 'localhost';

const emulatorConfigs = [
  {
    name: 'Firestore',
    url: `http://${EMULATOR_HOST}:8080/`,
    connect: () => connectFirestoreEmulator(db, EMULATOR_HOST, 8080),
    successMessage: '🔥 Connected to Firestore emulator',
    fallbackMessage: '📡 Using production Firestore (emulator not available)',
  },
  {
    name: 'Auth',
    url: `http://${EMULATOR_HOST}:9099/`,
    connect: () => connectAuthEmulator(auth, `http://${EMULATOR_HOST}:9099`, { disableWarnings: true }),
    successMessage: '🔐 Connected to Auth emulator',
    fallbackMessage: '🔑 Using production Auth (emulator not available)',
  },
  {
    name: 'Storage',
    url: `http://${EMULATOR_HOST}:9199/`,
    connect: () => connectStorageEmulator(storage, EMULATOR_HOST, 9199),
    successMessage: '📦 Connected to Storage emulator',
    fallbackMessage: '☁️ Using production Storage (emulator not available)',
  },
  {
    name: 'Functions',
    url: `http://${EMULATOR_HOST}:5001/`,
    connect: () => connectFunctionsEmulator(functions, EMULATOR_HOST, 5001),
    successMessage: '⚡ Connected to Functions emulator',
    fallbackMessage: '⚡ Using production Functions (emulator not available)',
  },
];

async function isEmulatorAvailable(url, timeout = 750) {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    await fetch(url, { mode: 'no-cors', signal: controller.signal });
    clearTimeout(timeoutId);
    return true;
  } catch (error) {
    return false;
  }
}

async function connectEmulatorsWhenAvailable() {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  await Promise.all(
    emulatorConfigs.map(async (config) => {
      const isAvailable = await isEmulatorAvailable(config.url);

      if (!isAvailable) {
        console.log(config.fallbackMessage);
        return;
      }

      try {
        config.connect();
        console.log(config.successMessage);
      } catch (error) {
        console.log(`${config.name} emulator connection failed; falling back to production.`, error);
      }
    })
  );
}

connectEmulatorsWhenAvailable();

export default app;
